/**
 * Spec 147 §4.5 / D7 — the marketing package's view of the credit
 * catalogue. It does not depend on the control-plane package, so it reads
 * the generated snapshot (`pricing-snapshot.generated.ts`, refreshed by
 * `pnpm --filter @repo/web gen:pricing-snapshot` and gated by
 * `check:pricing-snapshot`) and derives every card and sentence from it —
 * no price, credit count or bonus is typed in this package (spec 115
 * §3.2). apps/web still passes the live catalogue as props; these are the
 * defaults storybook and the tests render, and they are the same numbers.
 */
import { PRICING_SECTION } from './marketing-copy.js';
import { PRICING_SNAPSHOT } from './pricing-snapshot.generated.js';

export type SnapshotWorkspacePlan = (typeof PRICING_SNAPSHOT.workspace_plans)[number];
export type SnapshotMemberPlan = (typeof PRICING_SNAPSHOT.member_plans)[number];

/** "$20" / "$19.99" — the price tag in whole dollars unless there are cents. */
export function snapshotPrice(cents: number): string {
  const whole = cents % 100 === 0;
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

/** "2,300" — a credit count with thousands separators. */
export function snapshotCredits(n: number): string {
  return n.toLocaleString('en-US');
}

/** The paid account plans, in catalogue order (Free is the pay-per-use line, not a plan card). */
export function snapshotPaidPlans(): SnapshotWorkspacePlan[] {
  return PRICING_SNAPSHOT.workspace_plans.filter((p) => !p.is_free);
}

/** The pay-per-use card's bullets — the starter-grant line only while the grant exists. */
export function snapshotPayPerUseFeatures(): string[] {
  const grant = PRICING_SNAPSHOT.starter_grant_credits;
  return [
    'No plan — every action has a published price',
    ...(grant > 0 ? [`Start with ${snapshotCredits(grant)} free credits, no card`] : []),
    'Budgets and alerts included',
  ];
}

/** The home section's card set: pay per use (highlighted) then the paid account plans. */
export function snapshotWorkspaceCards() {
  return [
    {
      kind: 'usage' as const,
      overline: PRICING_SECTION.payPerUse.title,
      amount: '0',
      features: snapshotPayPerUseFeatures(),
      cta: PRICING_SECTION.payPerUse.cta,
      secondary: PRICING_SECTION.link,
      highlight: true,
    },
    ...snapshotPaidPlans().map((plan) => ({
      kind: 'plan' as const,
      overline: plan.label,
      amount: String(plan.price_usd_cents_monthly / 100),
      features: [
        `${snapshotCredits(plan.credits_per_month)} credits every month`,
        'Shared by everyone in the account',
        `${plan.storage_credits_gib} GiB Storage Credits a month`,
      ],
      cta: { label: `Start with ${plan.label}`, href: `/start?plan=${encodeURIComponent(plan.id)}` },
    })),
  ];
}

/**
 * Spec 147 §4.5 — the member-plans row is one sentence with a link, not
 * three tiles: "from $20 per person a month".
 */
export function snapshotMemberPlansSentence(): { line: string; from: string; href: string } {
  const cheapest = [...PRICING_SNAPSHOT.member_plans].sort(
    (a, b) => a.price_usd_cents_monthly - b.price_usd_cents_monthly,
  )[0];
  return {
    line: PRICING_SECTION.memberPlansLine,
    from: cheapest ? `From ${snapshotPrice(cheapest.price_usd_cents_monthly)} per person a month.` : '',
    href: '/pricing#member-plans',
  };
}
